import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import type {
  InboxCard, InboxCardsResponse, InboxApproveResult, InboxCardPriority,
} from '@/types/api'

export const INBOX_KEY = ['inbox', 'cards'] as const

export function inboxQueryKey() {
  return INBOX_KEY
}

/** Open Agent Inbox cards for the org, most urgent first (server-ordered). */
export function useInboxCards() {
  return useQuery({
    queryKey: INBOX_KEY,
    queryFn: async () => {
      const res = await api.get<InboxCardsResponse>('/inbox/cards')
      return res.cards
    },
    // The Inbox is filled by overnight producers; a short stale window is plenty
    // and we don't want to churn on every window focus.
    staleTime: 60_000,
  })
}

export interface ApproveArgs {
  cardId: string
  actionId?: string
}

/** Approve one of a card's proposed actions. Server executes + audits + resolves. */
export function useApproveCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ cardId, actionId }: ApproveArgs) =>
      api.post<InboxApproveResult>(`/inbox/cards/${cardId}/approve`, { action_id: actionId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: INBOX_KEY }),
  })
}

export interface ResolveArgs {
  cardId: string
  /** 'dismissed', an answer option id, or 'acknowledged'. */
  resolution?: string
}

/** Dismiss, acknowledge, or answer a card. */
export function useResolveCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ cardId, resolution }: ResolveArgs) =>
      api.post<InboxCard>(`/inbox/cards/${cardId}/resolve`, { resolution }),
    onSuccess: () => qc.invalidateQueries({ queryKey: INBOX_KEY }),
  })
}

/** Re-produce a dismissed/answered card by its idempotencyKey — the undo path.
 *  Safe because producing is idempotent; it restores the exact card to open.
 *  Not offered for approved cards (their action already executed). */
export function useReopenCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (card: InboxCard) =>
      api.post<InboxCard>('/inbox/cards', {
        type: card.type,
        priority: card.priority,
        title: card.title,
        body: card.body,
        sourceAgent: card.sourceAgent,
        idempotencyKey: card.idempotencyKey,
        entityLinks: card.entityLinks,
        proposedActions: card.proposedActions,
        options: card.options,
        expiry: card.expiry,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: INBOX_KEY }),
  })
}

// ── Whisper badge helpers (nav) ──────────────────────────────────────────────

export interface InboxWhisper {
  /** Total open cards. */
  count: number
  /** True when any open card is p1 — the nav badge goes red, otherwise soft. */
  urgent: boolean
}

export function inboxWhisper(cards: InboxCard[] | undefined): InboxWhisper {
  const open = cards ?? []
  return { count: open.length, urgent: open.some((c) => c.priority === 'p1') }
}

export const PRIORITY_LABEL: Record<InboxCardPriority, string> = {
  p1: 'Act today',
  p2: 'This week',
  p3: 'Whenever',
}

export const PRIORITY_ORDER: InboxCardPriority[] = ['p1', 'p2', 'p3']

/** Group open cards by priority, preserving the server order within each group. */
export function groupByPriority(cards: InboxCard[]): Array<{ priority: InboxCardPriority; cards: InboxCard[] }> {
  return PRIORITY_ORDER
    .map((priority) => ({ priority, cards: cards.filter((c) => c.priority === priority) }))
    .filter((g) => g.cards.length > 0)
}
